import type { UserInterface } from '@store/user/interfaces/userInterfaces';

import { getNestedValue, validateField } from './fieldValidators';
import { VALIDATION_RULES } from './validationRules';

// Main validation engine
export class ValidationEngine {
  private rules = VALIDATION_RULES;

  // Validate specific fields
  validateFields(
    user: UserInterface,
    fieldsToValidate: string[],
  ): { isValid: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {};

    fieldsToValidate.forEach(fieldPath => {
      const rule = this.rules[fieldPath];
      if (!rule) return; // No rule defined for this field

      const result = validateField(fieldPath, rule, user);

      if (!result.isValid && result.message) {
        errors[fieldPath] = result.message;
      }
    });

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  // Validate all user data
  validateUser(user: UserInterface): {
    isValid: boolean;
    errors: Record<string, string>;
  } {
    const allFields = Object.keys(this.rules);
    return this.validateFields(user, allFields);
  }

  // Validate a single field
  validateSingleField(
    user: UserInterface,
    fieldPath: string,
  ): { isValid: boolean; error?: string } {
    const rule = this.rules[fieldPath];
    if (!rule) return { isValid: true }; // No rule defined

    const result = validateField(fieldPath, rule, user);

    return {
      isValid: result.isValid,
      error: result.message,
    };
  }

  // Check if a field has a validation rule
  hasRule(fieldPath: string): boolean {
    return fieldPath in this.rules;
  }

  // Get field value
  getFieldValue(user: UserInterface, fieldPath: string): unknown {
    return getNestedValue(
      user as unknown as Record<string, unknown>,
      fieldPath,
    );
  }
}

// Create a singleton instance
export const validationEngine = new ValidationEngine();

// Field group definitions for step validation
export const FIELD_GROUPS = {
  personal: [
    'personalInfo.fullName',
    'personalInfo.documentType',
    'personalInfo.documentNumber',
    'personalInfo.passportExpiry',
    'personalInfo.civilStatus',
    'personalInfo.phone',
    'personalInfo.email',
    'professionalInfo.profession',
    'professionalInfo.occupation',
    'professionalInfo.retiredCountry',
    // FATCA fields - conditionally validated
    'fatcaInfo.usCitizen',
    'fatcaInfo.hasGreenCard',
    'fatcaInfo.stayed183',
    'fatcaInfo.tin',
    'fatcaInfo.taxCountry',
  ],

  residence: [
    // Address fields (currently read-only, but included for completeness)
    'address.street',
    'address.city',
    'address.state',
    'address.residence',
    // Extra nationality fields - conditionally validated
    'address.extraNationality.nationalityCRS',
    'address.extraNationality.taxResidenceCountryCRS',
    'address.extraNationality.hasTaxpayerNumber',
    'address.extraNationality.taxpayerNumber',
    'address.extraNationality.taxpayerNumberReason',
    'address.extraNationality.taxpayerNumberExplanation',
  ],

  job: [
    'jobInfo.companyName',
    'jobInfo.independentActivity',
    'jobInfo.initialDepositToOpenAccount',
    // PEP fields - conditionally validated
    'jobInfo.pepInfo.pepInstitution',
    'jobInfo.pepInfo.pepPosition',
    'jobInfo.pepInfo.pepPositionStartDate',
    'jobInfo.pepInfo.pepPositionEndDate',
    // Family Member PEP fields - conditionally validated
    'jobInfo.pepInfo.familyMemberPepRelation',
    'jobInfo.pepInfo.familyMemberPepPosition',
    'jobInfo.pepInfo.familyMemberPepDate',
  ],

  finance: [
    'financeInfo.monthlyIncome',
    'financeInfo.variableIncome',
    'financeInfo.countriesOfIncome',
    'financeInfo.deposits.amount',
    'financeInfo.deposits.transactionsQuantity',
    'financeInfo.deposits.countriesOfOrigin',
    'financeInfo.withdrawals.amount',
    'financeInfo.withdrawals.transactionsQuantity',
    'financeInfo.withdrawals.countriesOfOrigin',
  ],

  references: [],

  // Confirmation Information
  confirmation: ['confirmation.acceptedTerms'],

  // Modal field groups
  fatca: [
    'fatcaInfo.usCitizen',
    'fatcaInfo.hasGreenCard',
    'fatcaInfo.stayed183',
    'fatcaInfo.tin',
    'fatcaInfo.taxCountry',
  ],

  extraNationality: [
    'address.extraNationality.nationalityCRS',
    'address.extraNationality.taxResidenceCountryCRS',
    'address.extraNationality.hasTaxpayerNumber',
    'address.extraNationality.taxpayerNumber',
    'address.extraNationality.taxpayerNumberReason',
    'address.extraNationality.taxpayerNumberExplanation',
  ],

  pep: [
    'jobInfo.pepInfo.pepInstitution',
    'jobInfo.pepInfo.pepPosition',
    'jobInfo.pepInfo.pepPositionStartDate',
    'jobInfo.pepInfo.pepPositionEndDate',
  ],

  familyMemberPep: [
    'jobInfo.pepInfo.familyMemberPepRelation',
    'jobInfo.pepInfo.familyMemberPepPosition',
    'jobInfo.pepInfo.familyMemberPepDate',
  ],
};

// Convenience functions for step validation
export function validatePersonalStep(user: UserInterface) {
  return validationEngine.validateFields(user, FIELD_GROUPS.personal);
}

export function validateResidenceStep(user: UserInterface) {
  return validationEngine.validateFields(user, FIELD_GROUPS.residence);
}

export function validateJobStep(user: UserInterface) {
  return validationEngine.validateFields(user, FIELD_GROUPS.job);
}

export function validateFinanceStep(user: UserInterface) {
  return validationEngine.validateFields(user, FIELD_GROUPS.finance);
}

export function validateReferencesStep(_user: UserInterface): {
  isValid: boolean;
  errors: Record<string, string>;
} {
  // References are non-editable and come from backend, so always valid
  return { isValid: true, errors: {} };
}

export function validateConfirmStep(user: UserInterface) {
  return validationEngine.validateFields(user, FIELD_GROUPS.confirmation);
}

// Convenience functions for modal validation
export function validateModalFields(
  user: UserInterface,
  modalType: 'pep' | 'familyMemberPep' | 'fatca' | 'extraNationality',
) {
  const fieldGroup = FIELD_GROUPS[modalType];
  return validationEngine.validateFields(user, fieldGroup);
}

// Legacy compatibility functions (to maintain existing API)
export function validateUserFields(
  user: UserInterface,
  fieldsToValidate: string[],
): Record<string, string> {
  const result = validationEngine.validateFields(user, fieldsToValidate);
  return result.errors;
}

export function validateUser(user: UserInterface): Record<string, string> {
  const result = validationEngine.validateUser(user);
  return result.errors;
}
